import {
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography
} from "@mui/material";
import { useAuth } from "@sal/authentication.react";
import ActionCell from "components/personnelAndFleet/common/ActionCell";
import StatusCell from "components/personnelAndFleet/common/StatusCell";
import { PERSONNEL_STATUS_LABELS, PersonnelStatus } from "enums/PersonnelEnums";
import { useGetRoles } from "hooks/roles";
import { PersonnelUser } from "models/PersonnelUser";
import { useMemo, useState } from "react";
import { useColumnVisibility } from "hooks/useColumnVisibility";
import ColumnVisibilityPopover, { ColumnDef } from "components/ColumnVisibilityPopover";
import { HeadCell, Order, cellStyle, getComparator, useRangeSelection } from "../common/tableUtils";
import { getPersonnelUserActionItems, getStatusRank } from "./personnelUtils";
import CommunicationsCell from "./CommunicationsCell";
import RolesCell from "./RolesCell";

const checkboxColumnWidth = 48;
const actionColumnWidth = 40;

const headCells: (HeadCell<PersonnelUser> & { alwaysVisible?: boolean })[] = [
	{ id: "firstName", label: "FIRST NAME", width: 13, alwaysVisible: true },
	{ id: "lastName", label: "LAST NAME", width: 13, alwaysVisible: true },
	{ id: "email", label: "EMAIL", width: 22 },
	{ id: "fullPhone", label: "PHONE NUMBER", width: 12 },
	{ id: "communicationPreferences", label: "COMMUNICATIONS", width: 20 },
	{ id: "roles", label: "ROLE(S)", width: 12 },
	{ id: "active", label: "STATUS", width: 8 }
];

type PersonnelDataTableProps = {
	rows: PersonnelUser[];
	selectedRows: Set<number>;
	setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
	selectionDisabled?: boolean;
	onEditUser: (user: PersonnelUser) => void;
	onDuplicateUser: (user: PersonnelUser) => void;
	onDeactivateUser: (user: PersonnelUser) => void;
	onActivateUser: (user: PersonnelUser) => void;
	onApproveUser: (user: PersonnelUser) => void;
	canWrite?: boolean;
};

export default function PersonnelDataTable({
	rows,
	selectedRows,
	setSelectedRows,
	selectionDisabled = false,
	onEditUser,
	onDuplicateUser,
	onDeactivateUser,
	onActivateUser,
	onApproveUser,
	canWrite = false
}: PersonnelDataTableProps) {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof PersonnelUser>("firstName");
	const [visibleColumns, setVisibleColumns] = useColumnVisibility(
		["personnelAndFleet", "personnel", "grid"],
		headCells as ColumnDef[]
	);
	const auth = useAuth();
	const canReadRoles = auth.userHasPermission("Roles", "Read");
	const { roleOptions } = useGetRoles(canReadRoles);

	const sortedRows = useMemo(() => {
		if (orderBy === "active") {
			return [...rows].sort((a, b) => {
				const diff = getStatusRank(a) - getStatusRank(b);
				return order === "asc" ? diff : -diff;
			});
		}
		return [...rows].sort(getComparator(order, orderBy));
	}, [rows, order, orderBy]);

	const selectedVisibleCount = useMemo(
		() => rows.filter((r) => selectedRows.has(r.id)).length,
		[rows, selectedRows]
	);

	const isAllSelected = rows.length > 0 && selectedVisibleCount === rows.length;
	const isIndeterminate = selectedVisibleCount > 0 && selectedVisibleCount < rows.length;

	const handleRequestSort = (property: keyof PersonnelUser) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setSelectedRows((prev) => {
				const next = new Set(prev);
				rows.forEach((row) => next.add(row.id));
				return next;
			});
			return;
		}

		setSelectedRows((prev) => {
			const next = new Set(prev);
			rows.forEach((row) => next.delete(row.id));
			return next;
		});
	};

	const { handleRowClick } = useRangeSelection(sortedRows, selectedRows, setSelectedRows);

	return (
		<TableContainer
			sx={{
				flexGrow: 1,
				height: "100%",
				maxHeight: "100%",
				backgroundColor: "background.paper",
				overflow: "auto",
				borderRadius: 0,
				borderBottom: 1,
				borderTop: 1,
				borderColor: "border.hover"
			}}
		>
			<Table stickyHeader size="small">
				<TableHead>
					<TableRow>
						<TableCell
							sx={{
								width: `${checkboxColumnWidth}px`,
								minWidth: `${checkboxColumnWidth}px`,
								maxWidth: `${checkboxColumnWidth}px`,
								p: 0,
								textAlign: "center",
								borderRight: 1,
								borderColor: "border.hover",
								backgroundColor: "divider"
							}}
						>
							<Checkbox
								size="small"
								checked={isAllSelected}
								indeterminate={isIndeterminate}
								disabled={selectionDisabled}
								onChange={handleSelectAll}
							/>
						</TableCell>
						{headCells.filter((hc) => visibleColumns.has(hc.id)).map((headCell) => (
							<TableCell
								key={headCell.id}
								sortDirection={orderBy === headCell.id ? order : false}
								sx={{
									backgroundColor: "divider",
									borderRight: 1,
									borderColor: "border.hover",
									textWrap: "nowrap"
								}}
							>
								<TableSortLabel
									active={orderBy === headCell.id}
									direction={orderBy === headCell.id ? order : "asc"}
									onClick={() => handleRequestSort(headCell.id)}
									sx={{
										"& .MuiTableSortLabel-icon": { color: "text.secondary" }
									}}
								>
									<Typography variant="text12Uppercase">{headCell.label}</Typography>
								</TableSortLabel>
							</TableCell>
						))}
						<TableCell
							sx={{
								width: `${actionColumnWidth}px`,
								minWidth: `${actionColumnWidth}px`,
								maxWidth: `${actionColumnWidth}px`,
								backgroundColor: "divider",
								borderBottom: 1,
								borderColor: "border.hover",
								p: 1
							}}
						>
							<ColumnVisibilityPopover
								columns={headCells as ColumnDef[]}
								visibleColumns={visibleColumns}
								onApply={setVisibleColumns}
							/>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{sortedRows.map((row, index) => {
						const isSelected = selectedRows.has(row.id);
						const isActive = row.active === true;
						const actionItems = getPersonnelUserActionItems(row, {
							onEditUser,
							onDuplicateUser,
							onDeactivateUser,
							onActivateUser,
							onApproveUser
						});

						return (
							<TableRow
								key={row.id}
								selected={isSelected}
								hover
								sx={{ backgroundColor: "background.paper" }}
							>
								<TableCell sx={{ ...cellStyle, p: 0, textAlign: "center", width: `${checkboxColumnWidth}px`, minWidth: `${checkboxColumnWidth}px`, maxWidth: `${checkboxColumnWidth}px` }}>
									<Checkbox
										size="small"
										checked={isSelected}
										disabled={selectionDisabled}
										onChange={(e) =>
											handleRowClick(row.id, index, (e.nativeEvent as MouseEvent).shiftKey)
										}
									/>
								</TableCell>
								{visibleColumns.has("firstName") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.firstName || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("lastName") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.lastName || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("email") && (
									<TableCell sx={cellStyle}>
										{row.email ? (
											<Typography
												variant="text14"
												component="a"
												href={`mailto:${row.email}`}
												onClick={(e) => e.stopPropagation()}
												sx={{
													color: "primary.main",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" }
												}}
											>
												{row.email}
											</Typography>
										) : (
											<Typography variant="text14">-</Typography>
										)}
									</TableCell>
								)}
								{visibleColumns.has("fullPhone") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.fullPhone || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("communicationPreferences") && (
									<TableCell sx={cellStyle}>
										<CommunicationsCell preferences={row.communicationPreferences} />
									</TableCell>
								)}
								{visibleColumns.has("roles") && (
									<TableCell sx={cellStyle}>
										<RolesCell roles={row.roles} roleOptions={roleOptions} />
									</TableCell>
								)}
								{visibleColumns.has("active") && (
									<TableCell sx={cellStyle}>
										<StatusCell
											isPending={!row.cvWhenApproved}
											isActive={isActive}
											label={
												!row.cvWhenApproved
													? "Pending Approval"
													: row.active !== undefined
														? PERSONNEL_STATUS_LABELS[
																row.active ? PersonnelStatus.ACTIVE : PersonnelStatus.DEACTIVATED
															]
														: "-"
											}
										/>
									</TableCell>
								)}
								<TableCell
									sx={{
										...cellStyle,
										borderRight: "none",
										p: 0.5,
										width: `${actionColumnWidth}px`,
										minWidth: `${actionColumnWidth}px`,
										maxWidth: `${actionColumnWidth}px`
									}}
								>
									{canWrite && <ActionCell items={actionItems} />}
								</TableCell>
							</TableRow>
						);
					})}

					{sortedRows.length === 0 && (
						<TableRow>
							<TableCell
								colSpan={visibleColumns.size + 2}
								sx={{
									py: 4,
									textAlign: "center",
									color: "text.secondary"
								}}
							>
								No personnel found.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
